/**
 * RS Customizer — Striped Tote
 * Visual preview renderer for striped tote customization.
 * Fetches metaobject data (backgrounds, overlays, letter positioning settings),
 * injects a gallery preview slide, and renders font-based letter initials
 * as the user types — with fabric texture fill and a single product-level colour.
 *
 * Single-option product (Size only) — variant.title IS the tote size.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const LIQUID_VIEW = 'metaobject-striped-tote';

  const FONT_FAMILIES = {
    twoInitials: "'TwoInitialsFont', 'Arial Black', Arial, sans-serif",
    threeInitials: "'ThreeInitialsFont', 'Arial Black', Arial, sans-serif",
  };

  const DEFAULT_LETTER_COLOR = '#000000';

  let _injectedSlideId = null;
  let _metaobjectData = null;
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _currentToteSize = null;
  let _letterColor = DEFAULT_LETTER_COLOR;

  // ─── DOM helpers ────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) {
      text += field.value;
    });
    return text.trim().toUpperCase().replace(/[^A-Z]/g, '');
  }

  // ─── Data fetching ──────────────────────────────────────────────

  async function _fetchBaseConfig() {
    const response = await fetch(
      window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW
    );
    return response.json();
  }

  // ─── Background / size resolution ───────────────────────────────

  function _getBackgroundForVariant(toteSize) {
    if (!_backgroundsData || !toteSize) return null;
    return _backgroundsData[toteSize] || null;
  }

  function _getToteSizeForVariant(variantTitle) {
    if (!_metaobjectData || !variantTitle) return null;

    const productTitle = _metaobjectData.product_title || '';
    const variantKey = productTitle + ' - ' + variantTitle;
    const variantData = _metaobjectData.customizer_data
      ? _metaobjectData.customizer_data[variantKey]
      : null;

    // Single-option product — fall back to the raw variant title as the size.
    return variantData ? variantData.tote_size : variantTitle;
  }

  // ─── Gallery content container ──────────────────────────────────

  function _getContentContainer() {
    if (!_injectedSlideId) return null;

    const slide = document.querySelector(
      '[data-rs-injected="' + _injectedSlideId + '"]'
    );
    if (!slide) return null;

    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;

    let canvas = customizerView.querySelector('.rs-striped__canvas');
    if (!canvas) {
      canvas = document.createElement('div');
      canvas.className = 'rs-striped__canvas';
      customizerView.appendChild(canvas);
    }

    return canvas;
  }

  // ─── Colour management ─────────────────────────────────────────

  function _hex2rgb(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r: r, g: g, b: b };
  }

  function _applyColorCssVars(color) {
    if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) return;
    const rgb = _hex2rgb(color);
    const root = document.documentElement.style;
    root.setProperty('--customizer-striped-tote-selected-color', color);
    root.setProperty('--customizer-striped-tote-r', rgb.r);
    root.setProperty('--customizer-striped-tote-g', rgb.g);
    root.setProperty('--customizer-striped-tote-b', rgb.b);
  }

  // ─── Letter rendering ──────────────────────────────────────────

  function _renderLetters() {
    const text = _getMtoText();
    const container = _getContentContainer();
    if (!container) return;

    container
      .querySelectorAll('[data-rs-striped-letter]')
      .forEach(function (el) { el.remove(); });

    if (!text || text.length < 2 || text.length > 3) return;

    const letterCount = text.length;
    const settingsKey = letterCount === 2 ? 'two_letters' : 'three_letters';

    const sizeSettings = _metaobjectData.settings
      ? _metaobjectData.settings[_currentToteSize]
      : null;
    if (!sizeSettings) return;

    const positionSettings = sizeSettings[settingsKey];
    if (!positionSettings) return;

    Object.keys(positionSettings).forEach(function (letterKey, i) {
      if (i < text.length) {
        _createLetterElement(
          container,
          text[i],
          positionSettings[letterKey],
          i,
          letterCount
        );
      }
    });
  }

  function _createLetterElement(
    container,
    letterChar,
    settings,
    index,
    totalLetters
  ) {
    const el = document.createElement('div');
    el.setAttribute('data-rs-striped-letter', String(index));
    el.className =
      'rs-striped__letter ' +
      (totalLetters === 2
        ? 'rs-striped__letter--two'
        : 'rs-striped__letter--three');
    el.textContent = letterChar;

    el.style.fontFamily =
      totalLetters === 2
        ? FONT_FAMILIES.twoInitials
        : FONT_FAMILIES.threeInitials;

    _positionLetterElement(el, settings, container);
    container.appendChild(el);
    _scaleLetterElement(el, settings, container);

    el.setAttribute('data-stored-x', settings.x_position);
    el.setAttribute('data-stored-y', settings.y_position);
    el.setAttribute('data-stored-w', settings.width);
    el.setAttribute('data-stored-h', settings.height);
  }

  function _positionLetterElement(el, settings) {
    const centerX = settings.x_position + settings.width / 2;
    const centerY = settings.y_position + settings.height / 2;
    el.style.top = centerY + '%';
    el.style.left = centerX + '%';
    el.style.transform = 'translate(-50%, -50%)';
  }

  function _scaleLetterElement(el, settings, container) {
    // Force reflow so getBoundingClientRect reflects the unscaled glyph.
    void el.offsetWidth;

    const containerRect = container.getBoundingClientRect();
    const allocatedWidth =
      (settings.width / 100) * (containerRect.width || 800);
    const allocatedHeight =
      (settings.height / 100) * (containerRect.height || 600);

    const textRect = el.getBoundingClientRect();
    if (!textRect.width || !textRect.height) return;

    const scaleX = allocatedWidth / textRect.width;
    const scaleY = allocatedHeight / textRect.height;

    el.style.transform =
      'translate(-50%, -50%) scaleX(' + scaleX + ') scaleY(' + scaleY + ')';
  }

  // ─── Overlay rendering ─────────────────────────────────────────

  function _renderOverlay() {
    const container = _getContentContainer();
    if (!container) return;

    const existing = container.querySelector('.rs-striped__overlay');
    if (existing) existing.remove();

    if (!_currentToteSize || !_metaobjectData || !_metaobjectData.overlays) {
      return;
    }

    const overlayUrl = _metaobjectData.overlays[_currentToteSize];
    if (!overlayUrl) return;

    const overlay = document.createElement('div');
    overlay.className = 'rs-striped__overlay';

    const img = document.createElement('img');
    img.src = overlayUrl;
    img.alt = 'Customizer Overlay';
    overlay.appendChild(img);

    container.appendChild(overlay);
  }

  // ─── Resize handling ───────────────────────────────────────────

  function _handleResize() {
    document
      .querySelectorAll('[data-rs-striped-letter]')
      .forEach(function (el) {
        const canvas = el.closest('.rs-striped__canvas');
        if (!canvas) return;

        const settings = {
          x_position: parseFloat(el.getAttribute('data-stored-x')),
          y_position: parseFloat(el.getAttribute('data-stored-y')),
          width: parseFloat(el.getAttribute('data-stored-w')),
          height: parseFloat(el.getAttribute('data-stored-h')),
        };
        if (isNaN(settings.x_position)) return;

        _positionLetterElement(el, settings);
        _scaleLetterElement(el, settings, canvas);
      });
  }

  let _resizeTimer = null;
  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(_handleResize, 250);
  }

  // ─── Event handlers ────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderLetters();
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) {
      return;
    }

    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;
    _currentToteSize = _getToteSizeForVariant(_currentVariantTitle);

    // Core's variant handler replaces the gallery HTML — drop the old slide first.
    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForVariant(_currentToteSize);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    _renderOverlay();

    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
      if (_injectedSlideId) {
        window.RSProduct.gallery.navigate(_injectedSlideId);
      }
    }
  }

  // ─── Initialization ────────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    let baseConfig;
    try {
      baseConfig = await _fetchBaseConfig();
    } catch (err) {
      console.error('[StripedTote] Data fetch failed:', err);
      return;
    }

    if (baseConfig.success === false) {
      console.error('[StripedTote]', baseConfig.error);
      return;
    }

    _metaobjectData = baseConfig;
    _backgroundsData = baseConfig.backgrounds || {};
    _letterColor = baseConfig.letter_color || DEFAULT_LETTER_COLOR;
    _applyColorCssVars(_letterColor);

    const initialVariantId = String(
      window.RSProduct.config.initialVariantId || ''
    );
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    _currentVariantTitle =
      parts.length > 1 ? parts[parts.length - 1] : fullName;
    _currentToteSize = _getToteSizeForVariant(_currentVariantTitle);

    const backgroundUrl = _getBackgroundForVariant(_currentToteSize);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    _renderOverlay();

    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(
        window.theme.PUB_SUB_EVENTS.variantChange,
        _handleVariantChange
      );
    }

    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
